import React, { useEffect, useState, useRef, useContext } from "react";
import NextRouter from "next/router";
import { CardContent, Typography, Avatar, Popover } from "@mui/material";
import { toast, Bounce } from "react-toastify";
import CustomToastMessage from "../common/CustomToastMessage";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import { GlobalContext } from "components/context/GlobalContext";
import LinearProgress from "@mui/material/LinearProgress";
import { IconMaker } from "components/common/IconMaker";
import api from "components/settings/api";

const UserCard = ({
  name,
  userEmail,
  isVerified,
  dateCreated,
  currentUser,
  picture,
  setUser,
  user,
}) => {
  const appState = useContext(GlobalContext);
  const { updateUserData } = appState;
  const useStyles = makeStyles((theme) => ({
    popover: {
      pointerEvents: "none",
    },
    popOverPaper: {
      backgroundColor: "var(--accent-new-main)",
      margin: theme.spacing(1.2),
      padding: theme.spacing(1.2),
      color: "white",
    },
    nav_button: {
      borderRadius: "5px",
      maxWidth: "220px",
      width: "220px",
      margin: "20px 100px 2px 0px",
    },
  }));

  const [uploadPercentage, setUploadPercentage] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const [dateJoined, setDateJoined] = useState("");

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPicture, setCurrentPicture] = useState(picture);
  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const [imageSelected, setImageSelected] = useState("");

  const uploadImage = async () => {
    const formData = new FormData();
    formData.append("file", imageSelected);
    formData.append("id", currentUser.id);
    handleOpen();
    api
      .post("/api/v1/profiles/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          let percent = Math.floor((loaded * 100) / total);
          setUploadPercentage(percent);
        },
      })
      .then(() => {
        api
          .get("/api/v1/profiles/profile")
          .then((res) => {
            const { data } = res;
            if (data) {
              updateUserData(data);
              handleClose();
              setUploadPercentage(0);

              // updates profile photo
              setCurrentPicture(data.picture);

              // updates header photo
              user.picture = data.picture;
              setUser(user);
            }
          })
          .catch((err) => {
            throw new Error(err);
          });
      });
  };

  useEffect(() => {
    if (imageSelected !== "") {
      uploadImage();
    }
  }, [imageSelected]);

  useEffect(() => {
    setCurrentPicture(picture);
  }, [picture]);

  // End of Method
  const inputFile = useRef(null);
  const onButtonClick = () => {
    // `current` points to the mounted file input element
    inputFile.current.click();
  };

  useEffect(() => {
    setDateJoined(moment(dateCreated).format("MMMM Do, YYYY"));
  }, [dateCreated]);

  const open = Boolean(anchorEl);

  const allowAvatar = () => {
    try {
      const notify = (message) =>
        toast.info(<CustomToastMessage message={message} />, {
          autoClose: 2000,
          closeButton: true,
          hideProgressBar: false,
          transition: Bounce,
          position: "bottom-center",
        });
      notify("Please verify your email first.");
    } catch (error) {
      throw new Error(error);
    }
  };

  const viewYourPublicProfile = () => {
    if (currentUser.public_user_id !== undefined) {
      NextRouter.push(`/user/${currentUser.public_user_id}`);
    } else {
      NextRouter.push("/notfound");
    }
  };

  return (
    <>
      <Modal
        open={openModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "#ffffff",
            borderRadius: "8px",
            boxShadow: 24,
            outline: "none",
            border: "none",
            padding: "1.5rem",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              style={{
                fontStyle: "normal",
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "150%",
                letterSpacing: "0.15px",
                color: "#001B11",
              }}
            >
              Uploading...
            </Typography>
            <div>
              {uploadPercentage === 100 ? (
                <IconMaker name="finished-loading" height="24px" width="24px" />
              ) : (
                <>
                  <IconMaker name="stop-loading" height="24px" width="24px" />
                  <span style={{ padding: "6px" }} />
                  <IconMaker name="pause-loading" height="24px" width="24px" />
                </>
              )}
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 4fr",
              alignItems: "center",
              marginTop: "1rem",
            }}
          >
            <Typography
              style={{
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "150%",
                textAlign: "left",
                letterSpacing: "0.15px",
                color: "rgba(0, 27, 17, 0.7)",
              }}
            >
              {uploadPercentage}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadPercentage} />
          </div>
        </Box>
      </Modal>
      <div className="avatar--icon_profile">
        <div className="view-your-public-profile-link" style={{ zIndex: "2" }}>
          <Typography onClick={viewYourPublicProfile} aria-label="Public Profile">
            View public profile
          </Typography>
        </div>
        <CardContent className="profile--card_container">
          {picture ? (
            <div onClick={isVerified ? onButtonClick : allowAvatar}>
              <input
                className="my_file"
                type="file"
                ref={inputFile}
                onChange={(e) => setImageSelected(e.target.files[0])}
              />
              <div className="profile-image">
                <Avatar
                  src={currentPicture && currentPicture.replace(/^http:/, 'https:')}
                  alt="Avatar"
                  className="avatar--profile_image"
                />
              </div>
              <div className="edit">
                <svg
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="upload_image_icon"
                  aria-label="User image icon"
                >
                  <path
                    d="M7 0L5.17 2H2C0.9 2 0 2.9 0 4V16C0 17.1 0.9 18 2 18H18C19.1 18 20 17.1 20 16V4C20 2.9 19.1 2 18 2H14.83L13 0H7ZM10 15C7.24 15 5 12.76 5 10C5 7.24 7.24 5 10 5C12.76 5 15 7.24 15 10C15 12.76 12.76 15 10 15Z"
                    fill="white"
                  />
                </svg>
              </div>
            </div>
          ) : (
            ""
          )}
          <Typography color="textPrimary" gutterBottom component="span">
            <Typography
              sx={{
                fontSize: name && name?.length >= 24 ? "130% !important" : "inherit",
              }}
              className="card--profile_name"
            >
              {name}
            </Typography>
          </Typography>
          <div className="profile--card">
            {isVerified ? (
              <span className="profile--card_verified">Verified</span>
            ) : (
              <span className="profile--card_unverified">Unverified</span>
            )}
          </div>
          <Typography component="span">
            <Typography className="profile--card_join_on">
              Created on {dateJoined}
            </Typography>
          </Typography>
        </CardContent>
      </div>
    </>
  );
};

export default UserCard;
